<script setup lang="ts">
import { computed, provide, reactive, ref } from "vue";
import type { ComputedRef, UnwrapNestedRefs } from "vue";
import { defu } from "defu";
import SwProductListingFilter from "./SwProductListingFilter.vue";
import type {
  CmsElementProductListing,
  CmsElementSidebarFilter,
} from "@shopware/composables";
import { useCmsTranslations } from "@shopware/composables";
import { useCategoryListing } from "#imports";
import { onClickOutside } from "@vueuse/core";
import { type LocationQueryRaw, useRoute, useRouter } from "vue-router";
import type { Schemas, operations } from "#shopware";

const props = defineProps<{
  content: CmsElementProductListing | CmsElementSidebarFilter;
  listingType?: string;
}>();

type Translations = {
  listing: {
    filters: string;
    sort: string;
    resetFilters: string;
  };
};

let translations: Translations = {
  listing: {
    filters: "Filters",
    sort: "Sort",
    resetFilters: "Reset filters",
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const route = useRoute();
const router = useRouter();

const isSortMenuOpen = ref(false);
const {
  changeCurrentSortingOrder,
  filtersToQuery,
  getCurrentFilters,
  getCurrentSortingOrder,
  getInitialFilters,
  getSortingOrders,
  search,
} = useCategoryListing();

const sidebarSelectedFilters: UnwrapNestedRefs<{
  [key: string]: any;
}> = reactive<{
  manufacturer: Set<string>;
  properties: Set<string>;
  "min-price": string | number | undefined;
  "max-price": string | number | undefined;
  rating: string | number | undefined;
  "shipping-free": boolean | undefined;
}>({
  manufacturer: new Set(),
  properties: new Set(),
  "min-price": undefined,
  "max-price": undefined,
  rating: undefined,
  "shipping-free": undefined,
});

const showResetFiltersButton = computed<boolean>(() => {
  if (
    selectedOptionIds.value.length !== 0 ||
    sidebarSelectedFilters["max-price"] ||
    sidebarSelectedFilters["min-price"] ||
    sidebarSelectedFilters["rating"] ||
    sidebarSelectedFilters["shipping-free"]
  ) {
    return true;
  }

  return false;
});

const searchCriteriaForRequest: ComputedRef<Schemas["ProductListingCriteria"]> =
  computed(() => ({
    // turn Set to array and then into string with | separator
    manufacturer: [...(sidebarSelectedFilters.manufacturer as string[])]?.join(
      "|"
    ),
    // turn Set to array and then into string with | separator
    properties: [...(sidebarSelectedFilters.properties as string[])]?.join("|"),
    "min-price": sidebarSelectedFilters["min-price"] as number,
    "max-price": sidebarSelectedFilters["max-price"] as number,
    order: getCurrentSortingOrder.value as string,
    "shipping-free": sidebarSelectedFilters["shipping-free"] as boolean,
    rating: sidebarSelectedFilters["rating"] as number,
    search: "",
    limit: route.query.limit ? Number(route.query.limit) : 24,
  }));

for (const param in route.query) {
  if (sidebarSelectedFilters.hasOwnProperty(param)) {
    if (
      sidebarSelectedFilters[param] &&
      typeof sidebarSelectedFilters[param] === "object"
    ) {
      (route.query[param] as unknown as string)
        .split("|")
        .forEach((element) => {
          sidebarSelectedFilters[param].add(element);
        });
    } else {
      sidebarSelectedFilters[param] = route.query[param];
    }
  }
}

const onOptionSelectToggle = async ({
  code,
  value,
}: {
  code: string;
  value: string;
}) => {
  if (!["properties", "manufacturer"].includes(code)) {
    sidebarSelectedFilters[code] = value;
  } else {
    if (sidebarSelectedFilters[code]?.has(value)) {
      sidebarSelectedFilters[code]?.delete(value);
    } else {
      sidebarSelectedFilters[code]?.add(value);
    }
  }

  await executeSearch();
};

const executeSearch = async () => {
  await search(searchCriteriaForRequest.value);
  const query = filtersToQuery(searchCriteriaForRequest.value);
  delete query.limit; // this will remove limit from the url query but still use it in the search
  await router.push({
    query: query as LocationQueryRaw,
  });
};

const clearFilters = () => {
  sidebarSelectedFilters.manufacturer.clear();
  sidebarSelectedFilters.properties.clear();
  sidebarSelectedFilters["min-price"] = undefined;
  sidebarSelectedFilters["max-price"] = undefined;
  sidebarSelectedFilters["rating"] = undefined;
  sidebarSelectedFilters["shipping-free"] = undefined;
};

const currentSortingOrder = computed({
  get: (): string => getCurrentSortingOrder.value || "",
  set: async (order: string): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        order,
      },
    });

    await changeCurrentSortingOrder(order, {
      ...(route.query as unknown as operations["searchPage post /search"]["body"]),
      limit: route.query.limit ? Number(route.query.limit) : 24,
    });
  },
});

const selectedOptionIds = computed(() => [
  ...sidebarSelectedFilters.properties,
  ...sidebarSelectedFilters.manufacturer,
]);
provide("selectedOptionIds", selectedOptionIds);

async function invokeCleanFilters() {
  clearFilters();
  await executeSearch();
}
const isDefaultSidebarFilter =
  props.content.type === "sidebar-filter" &&
  props.content.config?.boxLayout?.value === "standard";
const dropdownElement = useTemplateRef("dropdownElement");
onClickOutside(dropdownElement, () => (isSortMenuOpen.value = false));
const isLoading = ref(true);
onMounted(async () => {
  isLoading.value = false;
});

const maxVisibleFilters = 7;
const showMoreFilters = ref(false);

const filtersToShow = computed(() => {
  const filters = getInitialFilters.value; // Access the array inside the computed ref
  return showMoreFilters.value ? filters : filters.slice(0, maxVisibleFilters);
});
</script>
<template>
  <ClientOnly>
    <div
      class="cust-accordion-wrap grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-0 sm:gap-x-8 lg:gap-0 pt-4"
    >
      <template v-if="getInitialFilters.length">
        <template v-if="!isLoading">
          <template
            v-for="filter in filtersToShow"
            :key="`${filter?.id || filter?.code}`"
          >
            <div
              v-if="
                filter?.code !== 'manufacturer' &&
                filter?.code !== 'rating' &&
                filter?.code !== 'shipping-free'
              "
              class="accordion-item overflow-hidden"
            >
              <SwProductListingFilter
                @select-filter-value="onOptionSelectToggle"
                :selected-filters="getCurrentFilters"
                :filter="filter"
                class="relative"
              />
              <hr class="border-t border-skin-light opacity-100 my-5" />
            </div>
          </template>

          <div
            v-if="
              !showMoreFilters && getInitialFilters.length > maxVisibleFilters
            "
          >
            <button
              type="button"
              class="btn-outline btn-sm flex relative group hover:w[110%] transition-all duration-300 ease-in-out"
              @click="showMoreFilters = true"
            >
              {{ $t("product.filter.moreFilters") }}
              <svg
                width="23"
                height="16"
                viewBox="0 0 23 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="ml-2 hidden group-hover:block"
              >
                <path
                  d="M22.7071 8.70711C23.0976 8.31658 23.0976 7.68342 22.7071 7.29289L16.3431 0.928932C15.9526 0.538408 15.3195 0.538408 14.9289 0.928932C14.5384 1.31946 14.5384 1.95262 14.9289 2.34315L20.5858 8L14.9289 13.6569C14.5384 14.0474 14.5384 14.6805 14.9289 15.0711C15.3195 15.4616 15.9526 15.4616 16.3431 15.0711L22.7071 8.70711ZM0 9H22V7H0V9Z"
                  fill="#B3E0C4"
                />
              </svg>
            </button>
          </div>
          <div
            v-if="showResetFiltersButton"
            class="w-full text-start mt-8 mb-2"
          >
            <button
              class="flex items-center h-10 px-6 py-3 text-sm bg-dark-bg text-white rounded-full hover:bg-red-bg"
              @click="invokeCleanFilters"
              type="button"
            >
              {{ translations.listing.resetFilters }}
              <span
                class="w-4 h-4 i-carbon-close-filled inline-block align-middle ml-2"
              ></span>
            </button>
          </div>
        </template>
        <div v-else class="loading-skeleton space-y-4">
          <div v-for="i in 4" :key="i" class="space-y-3">
            <!-- Accordion header -->
            <div
              class="h-10 bg-gray-200 rounded animate-pulse flex items-center px-4"
            >
              <div class="w-24 h-4 bg-gray-300 rounded"></div>
            </div>
            <!-- Accordion content -->
            <div class="px-0 space-y-2">
              <div
                v-for="j in 3"
                :key="j"
                class="h-4 bg-gray-200 rounded animate-pulse"
              ></div>
            </div>
          </div>
        </div>
        <!-- Accordion for Categories -->
      </template>
    </div>
  </ClientOnly>
</template>
